// =============================================================================
// Partner → OELOX API client
// =============================================================================
// Partner-web talks to Supabase directly for most reads/writes. Account
// creation and password resets additionally need the OELOX API because only the
// server holds the Supabase **service role** (Admin API) — a browser must never
// create Auth users.
// =============================================================================

import { getSupabaseClient } from "@veorest/api-client";

function apiBaseUrl(): string {
  const env = (import.meta as { env?: Record<string, string> }).env;
  const fromVite = env?.VITE_API_BASE_URL?.replace(/\/$/, "");
  if (fromVite) return fromVite;
  const runtime = (
    window as unknown as {
      OELOX_PUBLIC_CONFIG?: { VITE_API_BASE_URL?: string };
    }
  ).OELOX_PUBLIC_CONFIG?.VITE_API_BASE_URL?.replace(/\/$/, "");

  return runtime || "https://backend.oelox.com";
}

export class PartnerApiError extends Error {
  readonly code: string;
  readonly field?: string;
  readonly status: number;

  constructor(code: string, status: number, field?: string) {
    super(code);
    this.name = "PartnerApiError";
    this.code = code;
    this.status = status;
    this.field = field;
  }
}

/** Authenticated JSON call to the OELOX API (Bearer = partner Supabase session). */
export async function partnerApiPost<T>(
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const supabase = getSupabaseClient();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new PartnerApiError("not_authenticated", 401);
  }

  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (!res.ok) {
    throw new PartnerApiError(
      String(payload.error || "request_failed"),
      res.status,
      payload.field != null ? String(payload.field) : undefined
    );
  }

  return payload as T;
}
